import React, { useState, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';

const statusMap = {
  1: { label: 'BEE4181' /* Pend. Alocação */, color: '#808080', progress: 5 },
  2: {
    label: 'BEE1947' /* Pendente Corte de Cabos */,
    color: '#A0522D',
    progress: 10,
  },
  3: {
    label: 'BEE1365' /* Pendente Separação */,
    color: '#FF8C00',
    progress: 20,
  },
  4: { label: 'BEE1367' /* Em Separação */, color: '#1E90FF', progress: 30 },
  5: { label: 'BEE1768' /* Em Conferência */, color: '#8A2BE2', progress: 40 },
  6: { label: 'BEE1949' /* Pendente Docas */, color: '#00CED1', progress: 50 },
  7: {
    label: 'BEE1773' /* Pendente Romaneio */,
    color: '#DAA520',
    progress: 60,
  },
  8: {
    label: 'BEE1769' /* Pendente Consolidação */,
    color: '#20B2AA',
    progress: 70,
  },
  9: {
    label: 'BEE4184' /* Pend. Faturamento */,
    color: '#4169E1',
    progress: 80,
  },
  14: {
    label: 'BEE3687' /* Pendente Retirada */,
    color: '#4682B4',
    progress: 90,
  },
  10: { label: 'BEE1833' /* Finalizado */, color: '#32CD32', progress: 100 },
};

const headerGray = '#333333';

function hexToRGBA(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Configurações gerais
const PAGE_SIZE = 8; // Quantidade de documentos por card
const INTERVAL_MS = 10000; // Intervalo de atualização em ms

function OutboundOrderTracking({ data = [] }) {
  const [
    outboundOrderTrackingList,
    trackingDashboardColumnSequence,
  ] = data;

  const columnOrderString = trackingDashboardColumnSequence;

  const columnOrder = columnOrderString.split('|').map((s) => parseInt(s, 10));

  const groupedByStatus = columnOrder.reduce((acc, status) => {
    acc[status] = outboundOrderTrackingList.filter(
      (order) => order.status === status,
    );
    return acc;
  }, {});

  // Filtra e ordena os status com base na configuração de coluna.
  // Por padrão o cadastro de filiais já ordena, então a lógica abaixo é apenas pra garantir o sequenciamento
  const sortedColumnOrderStatus = columnOrder;

  const [pageIndexes, setPageIndexes] = useState({});
  const [isFading, setIsFading] = useState({});

  const breakTextEvery40Chars = (text) => {
    if (!text) return '';
    return text.match(/.{1,40}/g).join('<br />');
  };

  useEffect(() => {
    const initialPageIndexes = {};
    const initialFadeStates = {};
    sortedColumnOrderStatus.forEach((status) => {
      initialPageIndexes[status] = 0;
      initialFadeStates[status] = false;
    });
    setPageIndexes(initialPageIndexes);
    setIsFading(initialFadeStates);
  }, [data]);

  useEffect(() => {
    const interval = setInterval(() => {
      sortedColumnOrderStatus.forEach((status) => {
        const totalOrders = groupedByStatus[status]?.length || 0;
        if (totalOrders <= PAGE_SIZE) return;

        setIsFading((prev) => ({ ...prev, [status]: true }));

        setTimeout(() => {
          setPageIndexes((prev) => {
            const maxPage = Math.floor((totalOrders - 1) / PAGE_SIZE);
            const currentPage = prev[status] || 0;
            return {
              ...prev,
              [status]: currentPage >= maxPage ? 0 : currentPage + 1,
            };
          });

          setIsFading((prev) => ({ ...prev, [status]: false }));
        }, 400);
      });
    }, INTERVAL_MS);

    return () => clearInterval(interval);
  }, [groupedByStatus, sortedColumnOrderStatus]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '24px',
        padding: '20px',
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {sortedColumnOrderStatus.map((statusValue) => {
        const orders = groupedByStatus[statusValue];
        const status = statusMap[statusValue];
        if (!orders || !status) return null;

        const currentPage = pageIndexes[statusValue] || 0;
        const startIdx = currentPage * PAGE_SIZE;
        const visibleOrders = orders.slice(startIdx, startIdx + PAGE_SIZE);
        const fading = isFading[statusValue];

        return (
          <div
            key={statusValue}
            style={{
              backgroundColor: '#fff',
              borderRadius: '18px',
              boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
              minWidth: '430px',
              display: 'flex',
              flexDirection: 'column',
              padding: '24px',
              userSelect: 'none',
              flexShrink: 0,
              maxHeight: '85vh',
            }}
          >
            <div
              style={{
                backgroundColor: headerGray,
                color: '#fff',
                padding: '14px 18px',
                fontWeight: '700',
                fontSize: '1.35rem',
                borderRadius: '12px',
                marginBottom: '18px',
                textAlign: 'center',
                userSelect: 'text',
              }}
            >
              {I18n.t(status.label)}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                overflowY: 'auto',
                opacity: fading ? 0 : 1,
                transition: 'opacity 0.4s ease-in-out',
              }}
            >
              {visibleOrders.map((order, i) => {
                const { color } = status;

                return (
                  <div
                    key={i}
                    style={{
                      backgroundColor: hexToRGBA(color, 0.2),
                      border: `3px solid ${color}`,
                      borderRadius: '12px',
                      padding: '15px',
                      color: headerGray,
                      boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                      }}
                    >
                      <h2>
                        {I18n.t('BEE443' /* Documento */)}
                        #:
                        {order.orderNumber}
                      </h2>
                      <h2>
                        {I18n.t('BEE51' /* Pedido */)}
                        #:
                        {order.erpOrderCode}
                      </h2>
                      <h5
                        style={{
                          fontWeight: 'bold',
                          fontSize: '1.1rem',
                          textAlign: 'center',
                          lineHeight: '1.4',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: breakTextEvery40Chars(`${order.customer.name}`),
                        }}
                      />
                    </div>
                  </div>
                );
              })}

              {visibleOrders.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#888',
                    fontStyle: 'italic',
                    padding: '8px',
                  }}
                >
                  {I18n.t('BEE4386' /* Nenhum documento disponível. */)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default OutboundOrderTracking;
